import type { AnyRoute } from '@tanstack/react-router';

type LazyRouteState = { lazyFn?: unknown; _lazy?: unknown };
type PreloadableComponent = { preload?: unknown };

/**
 * Whether a route's code is already in memory, so rendering it fetches nothing.
 *
 * Two layers can be pending. A route with a `.lazy.tsx` file carries `lazyFn`, and the router
 * marks `_lazy` true once that file's options are merged in (none exist today; the check is
 * cheap insurance). A code-split component is a `lazyRouteComponent`, which exposes `preload()`
 * until its import resolves and then deletes it (`@tanstack/react-router`,
 * `lazyRouteComponent.tsx`), so a component without `preload` is either loaded or was never
 * split. A route with no component has nothing to load.
 *
 * Only the main component is inspected. A route with its own split `pendingComponent` would
 * report warm with that chunk still to load; no route declares one today.
 */
export function isRouteChunkWarm(route: AnyRoute): boolean {
    const state = route as unknown as LazyRouteState;
    if (state.lazyFn && state._lazy !== true) {
        return false;
    }
    const component = route.options.component as PreloadableComponent | undefined;
    return typeof component?.preload !== 'function';
}
